/*
 * Tabs and Read More for the 2026 PDP description section.
 *
 * A custom element rather than a DOMContentLoaded scan so the theme editor gets re-initialisation for free: removing
 * and re-inserting the section runs connectedCallback again. Kept separate from product-tabs.js, which still drives
 * the legacy `.product-tabs-container` section on the other product templates.
 */
(function () {
  var MOBILE_BREAKPOINT = 768;
  var RESIZE_DEBOUNCE = 250;

  var PdpTabs = class extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('.pdp-tabs__tab'));
      this.panels = Array.from(this.querySelectorAll('.pdp-tabs__panel'));
      this.readMoreEnabled = this.dataset.enableReadMore === 'true';

      if (!this.tabs.length) return;

      this.addEventListener('click', this._onClick.bind(this));
      this.addEventListener('keydown', this._onKeydown.bind(this));

      /* Liquid marks the first visible tab active. This is the safety net if that ever regresses, so the panel area
         can never render blank. */
      if (!this.tabs.some((tab) => tab.classList.contains('is-active'))) {
        this.select(this.tabs[0]);
      }

      if (this.readMoreEnabled) {
        this._onResize = this._debounce(this.refreshReadMore.bind(this), RESIZE_DEBOUNCE);
        window.addEventListener('resize', this._onResize);
        this.refreshReadMore();
      }
    }

    disconnectedCallback() {
      if (this._onResize) window.removeEventListener('resize', this._onResize);
    }

    get collapseHeight() {
      var mobile = window.innerWidth <= MOBILE_BREAKPOINT;
      var value = mobile ? this.dataset.collapseHeightMobile : this.dataset.collapseHeight;

      return parseInt(value, 10) || (mobile ? 200 : 300);
    }

    _debounce(fn, wait) {
      var timer;
      return function () {
        clearTimeout(timer);
        timer = setTimeout(fn, wait);
      };
    }

    _onClick(event) {
      var tab = event.target.closest('.pdp-tabs__tab');

      if (tab && this.contains(tab)) {
        this.select(tab);
        return;
      }

      var button = event.target.closest('.pdp-tabs__read-more');

      if (button && this.contains(button)) this.toggleReadMore(button);
    }

    _onKeydown(event) {
      if (!event.target.classList.contains('pdp-tabs__tab')) return;

      var offset = { ArrowRight: 1, ArrowLeft: -1, Home: 'first', End: 'last' }[event.key];

      if (!offset) return;

      event.preventDefault();

      var index;

      if (offset === 'first') {
        index = 0;
      } else if (offset === 'last') {
        index = this.tabs.length - 1;
      } else {
        index = (this.tabs.indexOf(event.target) + offset + this.tabs.length) % this.tabs.length;
      }

      this.select(this.tabs[index]);
      this.tabs[index].focus();
    }

    select(tab) {
      this.tabs.forEach(function (item) {
        var isActive = item === tab;

        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-selected', isActive ? 'true' : 'false');
        item.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      var panelId = tab.getAttribute('aria-controls');

      this.panels.forEach(function (panel) {
        panel.classList.toggle('is-active', panel.id === panelId);
      });
    }

    /*
     * Panels that are not active are display:none, so scrollHeight reports 0 and Read More would never appear on them.
     * Unhide just long enough to measure, off-screen so nothing flashes.
     *
     * Taking the panel out of flow leaves it shrink-to-fit, which on desktop resolves the grid and multi-column tab
     * layouts at a width the panel never actually has. Pinning the in-flow width keeps the measurement honest.
     */
    measure(element) {
      if (element.scrollHeight > 0) return element.scrollHeight;

      var panel = element.closest('.pdp-tabs__panel');

      if (!panel || getComputedStyle(panel).display !== 'none') return element.scrollHeight;

      var previous = panel.getAttribute('style') || '';

      panel.style.display = 'block';
      panel.style.visibility = 'hidden';
      panel.style.position = 'absolute';
      panel.style.width = this.getBoundingClientRect().width + 'px';

      var height = element.scrollHeight;

      panel.setAttribute('style', previous);

      return height;
    }

    refreshReadMore() {
      var threshold = this.collapseHeight;
      var self = this;

      this.panels.forEach(function (panel) {
        var collapse = panel.querySelector('.pdp-tabs__collapse');
        var button = panel.querySelector('.pdp-tabs__read-more');
        var prose = panel.querySelector('.pdp-tabs__prose');

        if (!collapse || !button || !prose) return;

        var height = self.measure(prose);
        var overflows = height > threshold;

        button.classList.toggle('is-visible', overflows);

        if (!overflows) {
          collapse.classList.remove('is-collapsed');
          collapse.style.maxHeight = '';
          return;
        }

        /* The desktop layouts change column count with the viewport, so an already-expanded panel has to be re-sized
           to the height it has at the new width, not the one it had at the old one. */
        if (collapse.classList.contains('is-expanded')) {
          collapse.style.maxHeight = height + 'px';
          return;
        }

        collapse.classList.add('is-collapsed');
        collapse.style.maxHeight = threshold + 'px';
      });
    }

    toggleReadMore(button) {
      var panel = button.closest('.pdp-tabs__panel');
      var collapse = panel.querySelector('.pdp-tabs__collapse');
      var prose = panel.querySelector('.pdp-tabs__prose');
      var label = button.querySelector('[data-read-more-label]');
      var expanding = collapse.classList.contains('is-collapsed');

      collapse.classList.toggle('is-collapsed', !expanding);
      collapse.classList.toggle('is-expanded', expanding);
      button.classList.toggle('is-expanded', expanding);
      button.setAttribute('aria-expanded', expanding ? 'true' : 'false');
      collapse.style.maxHeight = expanding ? this.measure(prose) + 'px' : this.collapseHeight + 'px';

      if (label) {
        label.textContent = expanding ? this.dataset.readLessLabel : this.dataset.readMoreLabel;
      }
    }
  };

  if (!window.customElements.get('pdp-tabs')) {
    window.customElements.define('pdp-tabs', PdpTabs);
  }
})();
